import {
  ActionStatus,
  ActionType,
  IActionResponseItem,
  IPolicyResponseItem,
  langauge,
} from "shared";
import { When } from "react-if";
import { Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useMemo } from "react";
import { ArrowPathIcon, ClockIcon } from "@heroicons/react/24/outline";

import { useActions, useActionTableModels } from "../../../hooks/useActions";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { Divider } from "../../../../common/Atoms/Divider";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { open } from "../../../slices/actionDrawer";
import { classNames } from "../../../../common/lib/classNames";
import { Button } from "../../../../common/Atoms/Button";

interface PolicyActionsViewProps {
  policyData?: IPolicyResponseItem | null;
  formDataDirty?: boolean;
}

const allowedRetrospective = [
  ActionType.ESCALATION,
  ActionType.LATE_FEE,
];
function getTimeRemaining(date: Date, tz: string) {
  const endOfDayOrg = DateTime.fromJSDate(date).setZone(tz).endOf(`day`);

  return endOfDayOrg.toRelative();
}

type GroupedAction = {
  [key in ActionStatus]?: IActionResponseItem[];
}

export function PolicyActionsView({
  policyData,
  formDataDirty,
}: PolicyActionsViewProps) {
  // useActions hook will check for presense of policyId in the URL
  const { data: actionsRes, isLoading, isFetching, refetch } = useActions();
  const currentOrg = useGetSelectedOrganisation();
  const dispatch = useDispatch();
  const { setCurrentTab, setSortModel, setFilterModel } = useActionTableModels();
  const navigate = useNavigate();

  if (!policyData) {
    return null;
  }

  const actions: GroupedAction = useMemo(() => {
    const result: GroupedAction = {};
    if (!actionsRes || !actionsRes.actions) return result;

    actionsRes.actions.forEach(action => {
      if (!result[action.status]) {
        result[action.status] = [];
      }

      result[action.status]?.push(action);
    });

    return result;
  }, [actionsRes]);

  function onActionClick(actionId: string) {
    dispatch(open(actionId));
  }

  function goToActions(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.stopPropagation();
    e.preventDefault();
    setCurrentTab(null);

    setSortModel([
      {
        field: `actionOn`,
        sort: `asc`,
      },
    ]);

    setFilterModel({
      items: [
        {
          field: `policyId`,
          operator: `equals`,
          value: policyData?.policy?.id,
          id: Math.round(Math.random() * 1e5),
        },
      ],
    });

    navigate(`/${currentOrg?.id}/actions?persist=true`);
  }

  function reloadActions() {
    refetch();
  }

  const hasActions = Object.keys(actions).length > 0;

  let overlayMessage = ``;
  let messageOnly = false;

  if (isLoading || isFetching) {
    overlayMessage = `Loading actions...`;
  }
  else if (formDataDirty) {
    overlayMessage = `Unsaved changes`;
    messageOnly = true;
  }

  return (
    <LoadingOverlay
      loading={ !!overlayMessage }
      message={ overlayMessage }
      messageOnly={ messageOnly }
    >
      <div className={ `w-full h-full` }>
        <When condition={  actionsRes?.actionCreateInProgress }>
          <div className={ `bg-orange-500 rounded-b-md` }>
            <Paragraph
              size={ `sm` }
              className={ `text-white px-2 py-2 text-center` }
            >
              { `Action generation is currently in progress!` }
            </Paragraph>
          </div>
        </When>
        { /* Helper text when no actions */ }
        <When condition={ !hasActions }>
          <div className={ `flex justify-center items-center min-h-[100px]` }>
            <div className={ `text-center` }>
              <Paragraph
                className={ `mt-6` }
                variant={ `help` }
              >
                { `No actions created for this policy` }
              </Paragraph>
              <Paragraph
                variant={ `help` }
              >
                { `Use the "Test & Preview" tab to find out why` }
              </Paragraph>

              <Button
                color={ `paidniceGreenOutline` }
                className={ `mt-6` }
                onClick={ e => {
                  e.preventDefault();
                  e.stopPropagation();

                  reloadActions();
                } }
              >
                { `Refresh` }
                <ArrowPathIcon className={ classNames(
                  `h-4 w-4 ml-1`,
                  isLoading || isFetching ? `animate-spin` : `cursor-pointer`,
                ) } />
              </Button>
            </div>
          </div>
        </When>

        { /* Helper text when actions exist */ }
        <When condition={ hasActions }>
          <div className={ `text-left mt-0.5` }>
            <Paragraph
              variant={ `help` }
            >
              { `A preview of some of the created actions for this policy. Updates may take a few minutes to appear.` }
            </Paragraph>

            <When condition={ !!actionsRes?.mostRecentUpdatedPending }>
              { () => (
                <div className={ `flex justify-between` }>
                  <Tooltip title={ DateTime.fromJSDate(new Date(actionsRes.mostRecentUpdatedPending)).toFormat(`yyyy LLL dd hh:mm:ss`) }>
                    <span>
                      <Paragraph
                        variant={ `help` }
                      >
                        { `Latest change: ${DateTime.fromJSDate(new Date(actionsRes.mostRecentUpdatedPending)).toRelative()}` }
                      </Paragraph>
                    </span>
                  </Tooltip>

                  <div>
                    <a
                      href={ `#` }
                      onClick={ e => {
                        e.preventDefault();
                        e.stopPropagation();

                        reloadActions();
                      } }
                    >
                      <ArrowPathIcon className={ classNames(
                        `h-4 w-4 ml-1`,
                        isLoading || isFetching ? `animate-spin` : `cursor-pointer`,
                      ) } />
                    </a>
                  </div>
                </div>
              ) }
            </When>
          </div>
        </When>

        { /* Render actions list */ }
        <When condition={ hasActions }>
          {
            Object.keys(actions).map(key => {
              const actionsForStatus = actions[key as ActionStatus];
              const notShownAmount = actionsRes.statusTotals[key as ActionStatus] - actionsForStatus.length;

              return (
                <div
                  key={ key }
                >
                  <Divider
                    text={ langauge.generic.action.status[key] }
                    weight={ `lighter` }
                    spacing={ `xs` }
                    textPosition={ `left` }
                    textWeight={ `bold` }
                  />
                  {
                    actionsForStatus.map(action => {
                      return (
                        <a
                          href={ `#` }
                          onClick={ e => {
                            e.stopPropagation();
                            e.preventDefault();
                            onActionClick(action.id);
                          } }
                          key={ action.id }
                        >
                          <div
                            className={ `grid grid-cols-12 gap-4 hover:bg-gray-50 rounded-md py-1` }
                          >
                            <div className={ `col-span-4 flex justify-start` }>
                              <Tooltip
                                title={ DateTime.fromJSDate(new Date(action.actionOn)).setZone(currentOrg.validatedTimezone).toFormat(`yyyy-MM-dd HH:mm ZZZZ`) }
                              >
                                <span>
                                  <Paragraph
                                    className={ `truncate` }
                                  >
                                    <Paragraph
                                      as={ `span` }
                                      variant={ `secondary` }
                                    >
                                      { `scheduled ` }
                                    </Paragraph>
                                    { DateTime.fromJSDate(new Date(action.actionOn)).setZone(currentOrg.validatedTimezone).toRelativeCalendar() }
                                    { /* { getPolicyInfo(action) } */ }
                                  </Paragraph>
                                </span>
                              </Tooltip>
                            </div>

                            <div className={ `col-span-6 flex justify-start` }>
                              <Paragraph
                                className={ `truncate` }
                                variant={ `secondary` }
                              >
                                { action.contact?.name }
                              </Paragraph>
                              <When condition={ !allowedRetrospective.includes(action.type) }>
                                <Paragraph
                                  as={ `span` }
                                  variant={ `help` }
                                  size={ `xs` }
                                  className={ `mr-1 flex items-center` }
                                >
                                  <Tooltip
                                    title={ `This action will be removed in ${getTimeRemaining(new Date(action.actionOn), currentOrg?.validatedTimezone)} hours` }
                                  >

                                    <ClockIcon className={ `h-4 w-4 ml-1` } />
                                  </Tooltip>
                                </Paragraph>
                              </When>
                            </div>

                            <div className={ `col-span-2 flex justify-center` }>
                              <Paragraph
                                className={ `truncate` }
                                variant={ `secondary` }
                              >
                                { action.invoice?.xero_number || action.quote?.number }
                              </Paragraph>
                            </div>
                          </div>
                        </a>
                      );
                    })
                  }

                  <When condition={ notShownAmount > 0 }>
                    <div className={ `flex justify-end` }>
                      <Paragraph
                        variant={ `help` }
                      >
                        { `+ ${notShownAmount} more...` }
                      </Paragraph>
                    </div>
                  </When>
                </div>
              );
            })
          }
          <div className={ `flex justify-center pt-4` }>
            <a
              href={ `#` }
              onClick={ goToActions }
            >
              <Paragraph
                variant={ `link` }
              >
                { `View all ${actionsRes?.total} actions for this policy` }
              </Paragraph>
            </a>
          </div>
        </When>
      </div>
    </LoadingOverlay>
  );
}
